// v3 scene-ref accessors. In Script v3 a scene's cast/props/location live as
// top-level scene fields — scene.actors[] / scene.props[] (arrays of
// {actor_id|prop_id, state_id?}) and scene.location ({location_id, state_id?} |
// null, single-valued) — NOT the legacy scene.context{} bag. These two functions
// are the ONLY place the patch ops touch that storage, so the whole context.*
// op family operates on v3 through the same {actors,locations,props} bag shape
// the ops already expect. `locations` is synthesized as a 0-or-1 array from the
// single scene.location so multi-location callers keep working (location stays
// single-valued: setSceneRefs writes locations[0] back as scene.location).
import { asList, type Dict, isDict, isList } from "../shared.js";

export function sceneRefs(scene: Dict): Dict {
  const loc = isDict(scene["location"]) ? (scene["location"] as Dict) : null;
  return {
    actors: isList(scene["actors"]) ? (scene["actors"] as Dict[]) : [],
    props: isList(scene["props"]) ? (scene["props"] as Dict[]) : [],
    locations: loc && String(loc["location_id"] ?? "").trim() ? [loc] : [],
  };
}

// Clean a single ref to v3 shape: keep the id key, keep state_id ONLY when it is
// a real (non-null, non-empty) materialized id — v3 validation rejects a
// state_id: null (it reads as "declared but not materialized").
function cleanRef(ref: Dict, idKey: string): Dict {
  const out: Dict = { [idKey]: ref[idKey] };
  const state = ref["state_id"];
  if (state != null && String(state).trim()) out["state_id"] = state;
  return out;
}

export function setSceneRefs(scene: Dict, refs: Dict): void {
  scene["actors"] = asList<Dict>(refs["actors"]).map((r) => cleanRef(r, "actor_id"));
  scene["props"] = asList<Dict>(refs["props"]).map((r) => cleanRef(r, "prop_id"));
  const loc = asList<Dict>(refs["locations"])[0];
  scene["location"] = loc && String(loc["location_id"] ?? "").trim() ? cleanRef(loc, "location_id") : null;
}
